import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2/src/sweetalert2';

import Button from 'react-bootstrap/Button';
import { selectAuditEntityId } from '@redux/selectors/AuditSelector';
import { getAuditLog } from '@redux/modules/audit';
import apiService from '@services/apiService';

import AuditTable from '@components/table/AuditTable';
import Panel from '@components/smartAdminUI/Panel';

const AuditLogPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const selectedListingId = useSelector(selectAuditEntityId);
  const urlSearchParams = new URLSearchParams(location.search);
  const listingIdParam = urlSearchParams.get('ListingID');
  const [listingId, setListingId] = useState(listingIdParam);
  const [filteredData, setFilteredData] = useState([]);
  const [canViewAuditLog, setCanViewAuditLog] = useState(false);

  const headers = [
    { key: 'TransactionID', label: 'Transaction ID' },
    { key: 'Transactiontype', label: 'Transaction Type' },
    { key: 'ClassName', label: 'Class Name' },
    { key: 'FieldName', label: 'Field Name' },
    { key: 'OldValue', label: 'Old Value' },
    { key: 'NewValue', label: 'New Value' },
    { key: 'RealUser', label: 'Real User' },
    { key: 'ImpersonatedUser', label: 'Impersonated User' },
    { key: 'ChangeTimeStamp', label: 'Change Timestamp' },
  ];

  const isNullOrEmpty = value => value === null || value === undefined || value === '';

  const handleFilteredData = data => {
    setFilteredData(data);
  };

  const handleExportAuditData = () => {
    const csvData = [
      headers.map(header => header.label),
      ...filteredData.map(logItem => headers.map(header => logItem[header.key])),
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `AuditLog for ${listingId} (${new Date().toLocaleString()}).csv`;
    link.click();
  };

  useEffect(async () => {
    if (isNullOrEmpty(listingIdParam)) return;
    // TODO: Think about moving this to a custom hook or possibly a HOC that is location aware
    //  and will catch routing before page actually shows making the second timed Swal unnecessary
    Swal.fire({
      allowEnterKey: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      position: 'center',
      html: 'Loading Listing Audit Log...',
      title: '<span class="fa-stack fa-2x text-secondary m-3"><i class="fal fa-circle-notch fa-spin fa-stack-2x"></i><i class="fas fa-history fa-stack-1x"></i></span>',
      showConfirmButton: false,
      didOpen: async () => {
        const response = await apiService.Acl.GetAclDetails('listing', {
          action: 'view',
          uniqueId: 0,
          listingId: listingIdParam,
        });
        const canAudit = response.formActions.AUDITLOG;
        setCanViewAuditLog(canAudit);
        if (canAudit) {
          setListingId(listingIdParam);
          dispatch(getAuditLog({ tableName: 'Listing', uniqueID: 0, entityID: listingIdParam }));
        } else {
          Swal.close();
          Swal.fire({
            allowEnterKey: false,
            allowEscapeKey: false,
            allowOutsideClick: false,
            position: 'center',
            title: 'Error -Unauthorized',
            text: 'Sorry, but you are not authorized to view this page.',
            icon: 'error',
            iconColor: 'var(--theme-danger)',
            showConfirmButton: false,
            timer: 3000,
          });
          history.push('/');
        }
      },
    });
  }, []);

  useEffect(() => {
    if (isNullOrEmpty(selectedListingId) && isNullOrEmpty(listingIdParam)) return;
    if (selectedListingId !== listingIdParam && !isNullOrEmpty(selectedListingId)) {
      setListingId(selectedListingId);
    } else {
      setListingId(listingIdParam);
    }
  }, [selectedListingId, listingIdParam]);

  return (canViewAuditLog && (
    <>
      <div className="subheader">
        <h1 className="subheader-title">
          <i className="subheader-icon fal fa-history mr-2" />
          Audit Log
        </h1>
      </div>
      <div className="row">
        <div className="col-lg-12">
          <Panel
            header={listingId ? `Details for [${listingId}]` : 'Details'}
            panelToolbar={(
              <>
                <Button
                  name="btnExportAuditData"
                  variant="primary btn-sm d-none d-sm-block"
                  onClick={handleExportAuditData}
                >
                  <i className="fas fa-file-csv mr-2" aria-hidden="true" />
                  Export CSV
                </Button>
              </>
            )}
          >
            <AuditTable sendFilteredDataToParent={handleFilteredData} />
          </Panel>
        </div>
      </div>
    </>
  ));
};

export default AuditLogPage;
